import { Play } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { BrandInitialMonogram } from "@/components/brand-initial-monogram"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-390 — Top-of-page media slot for the creator mission-detail page.
 *
 * Exactly one presentation renders, driven by the server-computed
 * `hasVideo` flag (the W9 / W9b discriminator — the FE NEVER re-derives it):
 *
 *   - **with-video (W9):** a 16:9 poster frame with a centered, non-functional
 *     play overlay. Playback is out of scope in v1 — tapping the overlay fires
 *     a "video coming soon" toast (no navigation). The poster is a *video*
 *     frame, so it legitimately stays 16:9. Falls back to the dark brand
 *     monogram when `posterUrl` is null / fails to load.
 *   - **without-video (W9b):** the mission cover in a 3:2 box (OKS-697 —
 *     `COVER_ASPECT_CLASS`) with the same dark monogram fallback. Mission
 *     detail is a priority cover surface (CTPO), so the cover must render at
 *     the same 3:2 frame the admin cropped — it is NOT piped through the
 *     shared 3:1 `BrandCoverBand` (that component stays brand-cover-only, per
 *     PRD "brand cover untouched"). The container keeps the
 *     `mission-detail-cover` testid so existing assertions hold.
 *
 * The play overlay is a real `<button>` with an `aria-label`; the cover /
 * poster images are decorative (`alt=""`) — the brand name is announced by
 * the page heading (a11y per UX Spec §6).
 */
export interface MediaSlotProps {
  hasVideo: boolean
  posterUrl: string | null
  coverImageUrl: string | null
  brandName: string
  className?: string
}

export function MediaSlot({
  hasVideo,
  posterUrl,
  coverImageUrl,
  brandName,
  className,
}: MediaSlotProps) {
  const { t } = useTranslation()
  // Poster / cover hide-on-error → degrade to the monogram fallback instead
  // of a broken-image icon (a stale signed URL or mid-render purge).
  const [posterBroken, setPosterBroken] = useState(false)
  const [coverBroken, setCoverBroken] = useState(false)

  if (!hasVideo) {
    // OKS-697 — mission cover at 3:2 (parity with every other surface), with
    // the dark monogram fallback when the cover is null / fails to load.
    const showCover = Boolean(coverImageUrl) && !coverBroken
    return (
      <div
        data-testid="mission-detail-cover"
        className={cn(
          "bg-surface-inverse relative w-full overflow-hidden rounded-xl",
          COVER_ASPECT_CLASS,
          className,
        )}
      >
        {showCover ? (
          <img
            src={coverImageUrl ?? undefined}
            alt=""
            loading="eager"
            decoding="async"
            onError={() => setCoverBroken(true)}
            data-testid="mission-detail-cover-image"
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <BrandInitialMonogram
              name={brandName.trim() || "?"}
              className="size-20 bg-white/10 text-3xl text-white"
              testId="mission-detail-cover-fallback"
            />
          </div>
        )}
      </div>
    )
  }

  const showPoster = Boolean(posterUrl) && !posterBroken

  function handlePlayTap() {
    // Non-functional in v1 — playback + the video source are out of scope.
    toast(t("creator_mission_detail.video.coming_soon"))
  }

  return (
    <div
      data-testid="mission-detail-media-video"
      className={cn(
        "bg-surface-inverse relative aspect-video w-full overflow-hidden rounded-xl",
        className,
      )}
    >
      {showPoster ? (
        <img
          src={posterUrl ?? undefined}
          alt=""
          loading="eager"
          decoding="async"
          onError={() => setPosterBroken(true)}
          data-testid="mission-detail-poster"
          className="size-full object-cover"
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <BrandInitialMonogram
            name={brandName.trim() || "?"}
            className="size-20 bg-white/10 text-3xl text-white"
            testId="mission-detail-poster-fallback"
          />
        </div>
      )}

      {/* Centered, non-functional play overlay (v1). Real button for a11y. */}
      <button
        type="button"
        onClick={handlePlayTap}
        aria-label={t("creator_mission_detail.video.play_aria")}
        data-testid="mission-detail-play"
        className="absolute inset-0 flex items-center justify-center focus-visible:outline-none"
      >
        <span className="bg-background/90 ring-background/40 flex size-14 items-center justify-center rounded-full shadow-lg ring-4 transition-transform hover:scale-105">
          <Play
            className="text-foreground size-6 translate-x-0.5"
            aria-hidden="true"
          />
        </span>
      </button>
    </div>
  )
}
